import React, { useState, useEffect } from "react";
import Layout from "@components/layout";
import showToast from "@utils/toast";
import { API_URL } from "src/service/config";

type GraphicAsset = {
  title: string;
  description: string;
  code: string;
  keyWords: string[];
  id?: string;
};

type GraphicResponseResult = {
  saved: GraphicAsset[];
  skipped: GraphicAsset[];
};

const defaultGraphic: GraphicAsset = {
  title: "",
  description: "",
  code: "",
  keyWords: [],
};

interface GraphicsFormProps {
  initialGraphics?: GraphicAsset[];
}

const GraphicsDeclarationForm: React.FC<GraphicsFormProps> = ({
  initialGraphics = [],
}) => {
  const [graphics, setGraphics] = useState<GraphicAsset[]>(
    initialGraphics.length > 0 ? initialGraphics : [defaultGraphic]
  );
  const [result, setResult] = useState<GraphicResponseResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [keywordDraft, setKeywordDraft] = useState("");

  useEffect(() => {
    if (initialGraphics.length) {
      setGraphics(initialGraphics);
    }
  }, [initialGraphics]);

  const handleChange = (
    index: number,
    field: keyof GraphicAsset,
    value: any
  ) => {
    const copy = [...graphics];
    copy[index][field] = value;
    setGraphics(copy);
  };

  const handleKeywordChange = (index: number, value: string) => {
    const updated = [...graphics];
    updated[index].keyWords = value.split(",").map((k) => k.trim());
    setGraphics(updated);
  };

  const addGraphic = () => {
    setGraphics((prev) => [...prev, { ...defaultGraphic }]);
  };

  const removeGraphic = (index: number) => {
    if (graphics.length === 1) return;
    const updated = [...graphics];
    updated.splice(index, 1);
    setGraphics(updated);
  };

  const addKeyword = (index: number) => {
    const draft = keywordDraft.trim();
    if (!draft) return;

    const updated = [...graphics];
    updated[index].keyWords.push(draft);
    setGraphics(updated);
    setKeywordDraft("");
  };

  const removeKeyword = (index: number, kId: number) => {
    const updated = [...graphics];
    updated[index].keyWords = updated[index].keyWords.filter(
      (_, i) => i !== kId
    );
    setGraphics(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const method = graphics.some((g) => g.id) ? "PUT" : "POST";
      const url = `${API_URL}/graphics/declare-graphics`;

      const formattedGraphics = graphics.map((g) => ({
        ...g,
        code: `${g.code}`,
      }));

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formattedGraphics),
      });

      const json = await res.json();
      setResult(json.result);
      showToast(json.message || "Graphics submitted successfully!", true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      showToast(message, false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 bg-white rounded shadow-md mt-5">
        <form onSubmit={handleSubmit}>
          <h2 className="text-xl font-semibold mb-6 text-center">
            {graphics.some((g) => g.id)
              ? "Edit Graphics"
              : "Declare Graphic Assets"}
          </h2>

          {graphics.map((graphic, i) => (
            <div
              className="bg-gray-100 border border-gray-100 p-4 rounded shadow-lg mb-4"
              key={graphic.id || i}
            >
              <div className="relative grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                <input
                  type="text"
                  placeholder="Title"
                  value={graphic.title}
                  onChange={(e) => handleChange(i, "title", e.target.value)}
                  required
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Description"
                  value={graphic.description}
                  onChange={(e) =>
                    handleChange(i, "description", e.target.value)
                  }
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Code"
                  value={graphic.code}
                  onChange={(e) => handleChange(i, "code", e.target.value)}
                  required
                  minLength={6}
                  maxLength={6}
                  className="form-input"
                />
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={keywordDraft}
                    onChange={(e) => setKeywordDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addKeyword(i);
                      }
                    }}
                    placeholder="Type keyword and press Enter"
                    className="form-input w-full"
                  />
                  <button
                    type="button"
                    onClick={() => addKeyword(i)}
                    className="bg-brand  text-white px-4 py-2 rounded"
                  >
                    +
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                  {graphic.keyWords.map((word, kIdx) => (
                    <span
                      key={kIdx}
                      className="bg-brand text-white px-2 py-1 rounded flex items-center"
                    >
                      {word}
                      <button
                        type="button"
                        onClick={() => removeKeyword(i, kIdx)}
                        className="ml-2 text-white hover:text-red-400"
                      >
                        ✖
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}

          <div className="flex items-center gap-4 mb-6">
            <button
              type="button"
              onClick={addGraphic}
              className="text-brand hover:underline"
            >
              + Add another graphic
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-brand text-white px-4 py-2 rounded hover:bg-brand-dark"
            >
              {submitting
                ? "Submitting..."
                : graphics.some((g) => g.id)
                ? "Update Graphics"
                : "Submit Graphics"}
            </button>
          </div>
        </form>

        {result && (
          <div className="mt-8 space-y-6">
            {/* Saved */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Saved Graphics</h3>
              <div className="flex overflow-x-auto gap-4 pb-2">
                {result.saved.map((item, idx) => (
                  <div
                    key={idx}
                    className="min-w-[180px] p-3 bg-green-50 rounded shadow shrink-0"
                  >
                    <p className="font-bold text-green-800">{item.title}</p>
                    <p className="text-sm text-gray-700">{item.description}</p>
                    <p className="text-xs text-green-600">
                      {item.code.toUpperCase()} • {item.keyWords.join(", ")}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Skipped */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Skipped Graphics</h3>
              <div className="flex overflow-x-auto gap-4 pb-2">
                {result.skipped.map((item, idx) => (
                  <div
                    key={idx}
                    className="min-w-[180px] p-3 bg-red-50 rounded shadow shrink-0"
                  >
                    <p className="font-bold text-red-800">{item.title}</p>
                    <p className="text-sm text-gray-700">{item.description}</p>
                    <p className="text-xs text-red-600">
                      {item.code.toUpperCase()} • {item.keyWords.join(", ")}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GraphicsDeclarationForm;
